<template>
	<div class="component-container component-download-rpa-studio">
		<modal v-if="active" @close="closeDownloadModal">
			<template #header>
				<p class="modal-title">
					Download RPA Studio
				</p>
			</template>
			<template #content>
				<div class="rpa-modal">
					<div>
						<p>Once installed, both the robot and Liberty RPA Studio will be ready on your desktop and you'll be creating process flows in no time.</p>
						<div class="row mt-25">
							<div>
								<label class="rpa-label" for="rpa-version">Version</label>
								<select id="rpa-version" v-model="download_data.version">
									<option v-for="(version_row) in getRpaReleasedVersionsReverse" :key="version_row.release" :value="version_row.release">
										{{ version_row.release }}
									</option>
								</select>
							</div>
							<div>
								<p class="text version-info">
									Latest patch {{ getLatestPatch }}
								</p>
							</div>
						</div>
						<div class="row mt-25">
							<div>
								<p class="rpa-label">
									Release Notes
								</p>
								<div class="release-links">
									<a v-for="(version_row) in getRpaReleasedVersions" :key="version_row.release" :href="`https://docs.netcall.com/docs/liberty-rpa/${version_row.release}/welcome/release-notes`" target="_blank">{{ version_row.release }}</a>
								</div>
							</div>
						</div>
						<div class="row mt-25">
							<div>
								<label class="rpa-label" for="rpa-licence">Bot Licence</label>
								<select id="rpa-licence" v-model="download_data.licence">
									<option value="none">
										None
									</option>
									<option value="new_development">
										New Development Licence
									</option>
									<option disabled>
										──────────────────
									</option>
									<option v-for="bot in bots" :key="bot.id" :value="bot.id">
										{{ bot.name }}
									</option>
								</select>
							</div>
						</div>
					</div>
					<img class="RobotIllustration" alt="Robot Illustration" src="/images/rpaDownload-mini_purple.svg" />
				</div>
			</template>
			<template #footer>
				<a class="btn btn-save" :href="getDownloadLink" target="_blank" title="Download RPA Studio" @click="requestDownloadRpaStudio"><fa class="icon" :icon="['fas', 'download']" /> Download RPA Studio</a>
				<a class="btn btn-cancel" href="#" title="Cancel" @click="closeDownloadModal">Cancel</a>
			</template>
		</modal>
	</div>
</template>

<script lang="ts">
	import { Options, Vue } from 'vue-class-component';
	import { GeneralHelper } from '../helper/general';
	import { HttpHelper } from '../helper/http';
	import Modal from './Modal.vue';
	import { BotObject } from '../types/bot';

	@Options({
		name: 'Download RPA Studio',
		components: { Modal },
		props: ['active', 'active_bot', 'from_sidebar', 'from_flow_page'],
		inject: ['http', 'helper'],
		emits: ['closeDownloadModal'],
	})
	export default class DownloadRpaStudio extends Vue {
		public http!: HttpHelper;
		public helper!: GeneralHelper;
		public active!: boolean;
		public active_bot!: any;
		public bots: BotObject[] = [];
		public from_sidebar!: boolean;
		public from_flow_page!: boolean;
		public download_link = '/api/studio/download';
		public download_data = {
			version: '',
			licence: 'none',
		};

		public get getDownloadLink(): string {
			let link = `${this.download_link}?version=${this.download_data.version}`;

			if (Number.isInteger(this.download_data.licence)) {
				link = `/api/rpa/bots/${this.download_data.licence}/download?version=${this.download_data.version}`;
			} else if (['none', 'new_development'].includes(this.download_data.licence)) {
				link = `${link}&licence=${this.download_data.licence}`;
			}  else {
				link = `/api/studio/download?version=${this.download_data.version}`;
			}

			return link;
		}

		public get getRpaReleasedVersions(): Array<Record<string, string>> {
			return this.$store.state.configuration.rpaReleasedVersions;
		}

		public get getRpaReleasedVersionsReverse(): Array<Record<string, string>> {
			return this.helper.deepClone(this.$store.state.configuration.rpaReleasedVersions).reverse();
		}

		public get getLastRelease(): string {
			return this.getRpaReleasedVersions[this.getRpaReleasedVersions.length - 1]?.release ?? '';
		}

		public get getBotId(): string {
			return this.active_bot?.id || 'none';
		}

		public get getLatestPatch(): string {
			return this.getRpaReleasedVersions.filter((version) => version.release === this.download_data.version)[0]?.latest || 'N/A';
		}

		public get isDevelopmentLicenceAvailable(): boolean {
			return this.$store.state.configuration.licensedDeveloperBots - this.$store.state.configuration.totalDeveloperBots > 0;
		}

		public async requestBots(): Promise<void> {
			const response = await this.http.get('/api/rpa/bots');
			if (response.status === 200) {
				const result = await response.json();
				this.bots = result.items;
			}
		}

		public populateDownloadData(): void {
			if (this.from_sidebar === true || this.from_flow_page === true) {
				this.download_data.licence = this.isDevelopmentLicenceAvailable ? 'new_development' : 'none';
			} else {
				this.download_data.licence = this.getBotId;
				this.download_link = `/api/rpa/bots/${this.active_bot?.id}/download`;
			}
			this.download_data.version = this.getLastRelease;
		}

		public requestDownloadRpaStudio(): void {
			this.$emit('closeDownloadModal');
		}

		public closeDownloadModal(): void {
			if (this.from_flow_page === true) {
				window.localStorage.setItem('lc:dashboard_hide_download', 'yes');
			}

			this.$emit('closeDownloadModal');
		}

		public async mounted(): Promise<void> {
			await this.requestBots();
			this.$watch(() => this.active_bot, () => {
				this.populateDownloadData();
			});
			if (this.from_flow_page === true) {
				const show_modal = window.localStorage.getItem('lc:dashboard_hide_download');
				if (show_modal !== null && show_modal === 'yes') {
					this.closeDownloadModal();
				} else {
					this.populateDownloadData();
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import '../assets/styles/base';

	.modal-content .rpa-modal {
		display: grid;
		grid-template-columns: 4fr 3fr;
		gap: 2rem;
		margin-bottom: 1rem;
		p {
			font-size: 16px;
			line-height: 1.5;
			margin-bottom: 1rem;
		}
		.RobotIllustration {
			margin-top: -10px;
		}

		.row {
			display: flex;

			.rpa-label {
				display: block;
				margin-bottom: 10px;
				font-weight: 600;
				font-size: 14px;
			}

			.version-info {
				margin-left: 10px;
				padding-top: 32px;
				color: $secondary-colour;
			}

			.release-links {
				padding-bottom: 5px;
			}

			.release-links a {
				padding: 5px 10px;
				background-color: rgba($create-color, 0.1);
				outline: 2px dotted $create-color;
				outline-offset: -2px;
				display: inline-block;
				width: auto;
				font-family: "Inter", sans-serif;
				font-weight: bold;
				font-size: 12px !important;
				border-radius: 5px;
				line-height: 1.1;
				transition: all ease-in-out .5s;
				margin-right: 0.75rem;
				&:hover {
					background-color: rgba($create-color, 1);
					outline: 2px solid $create-color;
					outline-offset: -2px;
					color: white !important;
					transition: all ease-in-out .3s;
				}
			}
		}
	}
</style>
